import Router from "next/router"
import axios from "axios"

import updateCart from "@secureo/common/utils/commerceTools/graphql/mutations/updateCart"
import { custom, distributionChannel } from "@secureo/common/reducers/productDetailPageReducer"
import { getOrCreateCart, updateTaxes } from "@secureo/common/context/AppContext/actions"
import getLanguageAndCountryCodeFromLocale from "@secureo/common/utils/i18n/getLanguageAndCountryCodeFromLocale"
import { DispatchLockingSystemReducer, LockingSystemReducerState } from ".."
import { AppContextState } from "@secureo/common/context/AppContext/AppContext"
import {
	CustomFieldInput,
	MyCartUpdateAction,
	MyLineItemDraft,
	SetCartCustomType,
} from "@secureo/common/typings/generated/commercetools-graphql"
import { ProviderContext } from "notistack"

const setCustomType: SetCartCustomType = {
	type: {
		typeId: "type",
		key: "customOrder",
	},
}

export const addToCart = async (
	dispatch: DispatchLockingSystemReducer,
	reducerState: LockingSystemReducerState,
	appContextState: AppContextState,
	enqueueSnackbar: ProviderContext["enqueueSnackbar"],
	saveLockingSystem: () => Promise<string>,
) => {
	dispatch({ type: "SET_IS_LOADING", payload: true })

	try {
		const {
			locale,
			countryCode,
			name = "",
			lockingComponentsUuids,
			keyComponentsUuids,
			componentsByUuid,
			charges: { totalGrossChargeCents, numberOfFreeIncludedKeys },
			isFrozen,
		} = reducerState

		let lockingSystemId = reducerState.shoppingListId

		if (!isFrozen) {
			// Save lockingSystem, if user owns lockingSystem
			lockingSystemId = await saveLockingSystem()
		}

		const { accessToken, dispatch: dispatchAppContextState } = appContextState

		const { language } = getLanguageAndCountryCodeFromLocale(locale)
		let cart = await getOrCreateCart(appContextState, language, countryCode, "EUR")

		const { lockingSystems } = cart

		const cartAlreadyContainsLockingSystem = lockingSystems.some(
			(lockingSystem) => lockingSystem.id === lockingSystemId,
		)

		if (cartAlreadyContainsLockingSystem) {
			// Remove lockingComponent
			const [lockingComponent] =
				cart.lockingSystems.find((lockingSystem) => lockingSystem.id === lockingSystemId)
					?.lockingComponentLineItems ?? []

			const cartUpdateActions: MyCartUpdateAction[] = [
				{
					removeLineItem: {
						lineItemId: lockingComponent.id,
					},
				},
			]
			console.info(`Removing existing lockingSystem with id ${lockingSystemId} from cart`)
			cart = await updateCart(accessToken, cart.id, cart.version, locale, cartUpdateActions)
			cart = await updateTaxes(accessToken, dispatchAppContextState, cart.id, countryCode)
		}

		// Add all components
		const lineItemDrafts = [...lockingComponentsUuids, ...keyComponentsUuids]
			.map((uuid) => {
				const component = componentsByUuid[uuid]

				const {
					productId,
					variantId,
					quantity,
					configurationsByConfigurationGroup,
					name: lockingSystemComponentName,
				} = component

				const uuidCustomField: CustomFieldInput = {
					name: "uuid",
					value: `"[${productId}]-${uuid}"`,
				}

				const drafts: MyLineItemDraft[] = [
					{
						distributionChannel,
						productId,
						variantId,
						quantity,
						custom: {
							...custom,
							fields: [
								uuidCustomField,
								{
									name: "lockingSystemId",
									value: `"${lockingSystemId}"`,
								},
								{
									name: "lockingSystemComponentName",
									value: `"${lockingSystemComponentName}"`,
								},
							],
						},
					},
				]

				// Add configurations
				for (const configurationGroupProductsBySku of Object.values(
					configurationsByConfigurationGroup,
				)) {
					const configurations = Object.values(configurationGroupProductsBySku)

					for (const configuration of configurations) {
						const { productId, variantId } = configuration

						const configurationQuantity = quantity * configuration.quantity

						const draft: MyLineItemDraft = {
							productId,
							variantId,
							quantity: configurationQuantity,
							distributionChannel,
							custom: {
								...custom,
								fields: [
									uuidCustomField,
									{
										name: "lockingSystemId",
										value: `"${lockingSystemId}"`,
									},
								],
							},
						}

						drafts.push(draft)
					}
				}

				return drafts
			})
			.flat()

		const cartUpdateActions: MyCartUpdateAction[] = [
			{ setCustomType },
			...lineItemDrafts.map((addLineItem) => ({
				addLineItem,
			})),
		]

		enqueueSnackbar("Bitte warten Sie während Ihre Schließanlage verarbeitet wird.", {
			variant: "info",
		})
		await updateCart(accessToken, cart.id, cart.version, locale, cartUpdateActions)
		await axios.get(
			`/api/lockingSystem/addLockingSystemCustomLineItemToCart?cartId=${cart.id}&lockingSystemId=${lockingSystemId}&customName=${name}&numberOfFreeIncludedKeys=${numberOfFreeIncludedKeys}&totalGrossChargeCents=${totalGrossChargeCents}`,
		)

		// Taxes need to be updated 2 times, because component taxRates need be set in order to calculate charges correctly
		await updateTaxes(accessToken, dispatchAppContextState, cart.id, countryCode)
		await updateTaxes(accessToken, dispatchAppContextState, cart.id, countryCode)

		dispatch({ type: "SET_IS_LOADING", payload: false })
		Router.push("/[locale]/cart", `/${locale}/cart`)
	} catch (err) {
		console.error(err)

		dispatch({ type: "SET_IS_LOADING", payload: true })

		enqueueSnackbar("Etwas ist schiefgelaufen", {
			variant: "error",
		})
	}
}
